import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';

import AuthService from '../Services/AuthService';
import RequestBuilder from '../Lib/Requests';
import { BrandPrimarySpinningCircle } from './LoadingCircle';

export default function WithAuth(AuthComponent) {
  function Authenticated(props) {
    const [state, updateState] = useState({ isLoading: true });

    const history = useHistory(),
      location = useLocation(),
      { scheduleId } = useParams();

    const api = useMemo(() => new RequestBuilder(), []),
      auth = useMemo(() => new AuthService(), []),
      token = useMemo(() => auth.getToken(), [auth]);

    const targetPath = useMemo(
        () => `${location.pathname}${location.search}`,
        [location.pathname, location.search]
      ),
      loginRedirect = useMemo(
        () => ({ pathname: '/login', state: { targetPath } }),
        [targetPath]
      );

    const validateToken = useCallback(async () => {
      try {
        const source = CancelToken.source();
        await api.request(auth.buildValidateRequest(source.token));
        updateState((prevState) => ({ ...prevState, isLoading: false }));
      } catch (err) {
        if (isCancel(err)) return;
        auth.logout();
        history.replace(loginRedirect);
      }
    }, [api, auth, loginRedirect, history]);

    useEffect(() => {
      if (!token) return history.replace(loginRedirect);
      validateToken();
    }, [loginRedirect, history, token, validateToken]);

    return state.isLoading ? (
      <div className="vh-100 vw-100 d-flex align-items-center justify-content-center">
        <BrandPrimarySpinningCircle size="5x" />
      </div>
    ) : (
      <AuthComponent
        {...props}
        history={history}
        location={location}
        scheduleId={scheduleId} //TODO: refactor CreateEditSchedule to functional component
      />
    );
  }
  Authenticated.displayName = `Authenticated(${
    AuthComponent.displayName ?? AuthComponent.name
  })`;
  Authenticated.propTypes = {
    history: PropTypes.shape({ replace: PropTypes.func }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  };

  return Authenticated;
}
